import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import * as L from 'leaflet';
import { LabelGenerationService } from '../../../services/label-generation.service';
import { NotificationService } from '../../../services/notification.service';
import { WaybillDocumentService } from '../../../services/waybill-document.service';
import { ShipmentManagementService, ClientsBrands } from '../../../services/shipment-management.service';
import { ComParcelEvent, PARCEL_EVENT_PRESENTATIONS } from '../../../interfaces/ComParcelEvent.interfaces';
import { WaybillEventPresentation } from '../../../interfaces/ComWaybill.interfaces';
import { Image, IMAGE_TYPE_LABELS } from '../../../interfaces/Image.interfaces';
import { Carriers } from '../../../interfaces/Carriers.interfaces';
import { COM_PARCEL_STATUS_LABELS } from '../../../interfaces/ComParcel.interfaces';
import { Tracking } from '../../../interfaces/Tracking.interfaces';
import { LoadingListsAction, ParcelToInvalid } from '../invalidate-loading-lists-popup/invalidate-loading-lists-popup.component';

/**
 * Punto di transito dell'instradamento georeferenziato sulla mappa
 * (magazzino intermedio o dealer di destinazione finale)
 */
export interface RoutingMapPoint {
  /** Progressivo del punto sulla mappa (1-based) */
  sequence: number;
  /** Codice magazzino o codice dealer */
  code: string;
  /** Nome da mostrare nella label */
  name: string;
  /** Indirizzo esteso (mostrato nel popup) */
  address?: string;
  latitude: number;
  longitude: number;
  kind: 'warehouse' | 'dealer';
  /**
   * Stato della tappa che arriva sul punto (per il punto di partenza,
   * quello della tappa che ne parte): allinea colore e stato a quelli della tratta
   */
  status?: string;
  /** Tipo della tappa associata al punto */
  type?: string;
}

/** Tile OpenStreetMap usate da tutti gli stili della mappa instradamento */
const ROUTING_MAP_OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const ROUTING_MAP_OSM_ATTRIBUTION = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/** Colore dei punti fuori percorso (misrouting) sulla mappa: lo stesso delle deviazioni nella vista "Percorso" */
const ROUTING_MAP_MISROUTING_COLOR = '#59359a';

/** Stili di mappa selezionabili nella vista mappa dell'instradamento */
export type RoutingMapStyleId = 'light' | 'gray' | 'street' | 'dark';

/** Definizione di un tile layer selezionabile */
export interface RoutingMapStyle {
  id: RoutingMapStyleId;
  label: string;
  icon: string;
  url: string;
  attribution: string;
  /** Classe CSS applicata al contenitore: filtra le tile OSM per ottenere lo stile */
  cssClass: string;
  /** True sugli stili scuri: le label vengono adattate */
  isDark?: boolean;
}

/**
 * Tratta tra due punti di transito, disegnata come linea sulla mappa
 */
export interface RoutingMapLeg {
  from: RoutingMapPoint;
  to: RoutingMapPoint;
  /** Numero della tappa (parcelComplementarySequence) */
  sequence: number;
  type: string;
  status: string;
  carrierCode?: string;
  /** Distanza in linea d'aria tra i due punti, in metri */
  distance: number;
  /** Inizio evento della tappa */
  eventStart?: string;
  /** Fine evento della tappa */
  eventEnd?: string;
}

/**
 * Punto di transito nella vista "Percorso" dell'instradamento
 * (magazzino intermedio o dealer di destinazione finale)
 */
export interface RoutingChainPoint {
  /** Codice magazzino o codice dealer (vuoto se il punto non è definito sulla tappa) */
  code: string;
  name: string;
  kind: 'warehouse' | 'dealer';
  /** Località (città e provincia), se nota */
  place?: string;
  /**
   * Stato della tappa che arriva sul punto (per il punto di partenza,
   * quello della tappa che ne parte)
   */
  status?: string;
}

/** Tratta tra due punti di transito nella vista "Percorso", con l'azienda trasportatrice */
export interface RoutingChainLeg {
  /** Numero della tappa (parcelComplementarySequence) */
  sequence: number;
  /** Numero progressivo del trasporto (1, 2, 3...): conta solo le tratte disegnate, senza le soste */
  transport: number;
  status: string;
  carrierCode?: string;
  /** Nome dell'azienda trasportatrice (il codice, se il vettore non è in anagrafica) */
  carrierName?: string;
}

/** Finestra entro cui gli eventi di danno dello stesso magazzino finiscono nello stesso gruppo */
const DAMAGE_GROUP_WINDOW_MS = 6 * 60 * 60 * 1000;

/**
 * Altezza di base di una corsia di deviazione, altezza di un'etichetta di danno e spazio tra pallino
 * ed etichetta: devono coincidere con --detour-lane-height, --damage-chip-height e --detour-damage-gap del CSS
 */
const ROUTING_DETOUR_LANE_HEIGHT = 104;
const ROUTING_DAMAGE_CHIP_HEIGHT = 52;
const ROUTING_DETOUR_DAMAGE_GAP = 14;
const ROUTING_DAMAGE_CHIP_SPACING = 6;

/**
 * Gruppo di eventi di danno (DAMAGE) riportato nella vista "Percorso": gli eventi dello stesso
 * magazzino registrati entro 6 ore dal primo del gruppo
 */
export interface RoutingChainDamage {
  /** Data di registrazione del primo evento del gruppo */
  date?: string;
  /** Data dell'ultimo evento del gruppo (solo se il gruppo ha più eventi) */
  endDate?: string;
  /** Numero di eventi del gruppo */
  count: number;
  /** Chi ha segnalato il danno (più nomi, se diversi) */
  createdBy?: string;
  /** Magazzino dell'evento: valorizzato solo se non è un punto del percorso */
  place?: string;
}

/**
 * Gruppo di eventi di picking (palmare) di un magazzino, registrati entro 6 ore dal primo del gruppo
 */
export interface RoutingChainPicking {
  /** Data di registrazione del primo evento del gruppo */
  date?: string;
  /** Numero di eventi del gruppo */
  count: number;
}

/** Nodo della vista "Percorso": un punto di transito e la tratta che ne parte verso il successivo */
export interface RoutingChainNode {
  point: RoutingChainPoint;
  leg?: RoutingChainLeg;
  /** Eventi di danno registrati nel punto */
  damages: RoutingChainDamage[];
  /** Eventi di picking registrati nel punto */
  pickings: RoutingChainPicking[];
}

/** Tappa MISROUTING di una deviazione nella vista "Percorso" */
export interface RoutingChainDetourLeg {
  /** Nome del punto di arrivo (punto fuori percorso, se la deviazione prosegue) */
  to: string;
  /** Località (città e provincia) del punto di arrivo, se nota */
  toPlace?: string;
  /** Codice del magazzino di arrivo (vuoto se l'arrivo non è un magazzino) */
  toCode?: string;
  carrierName: string;
  /** Dettaglio della tappa, mostrato come tooltip */
  title: string;
  /** Gruppi di danno registrati nel magazzino di arrivo della tappa, legati al vettore della tappa */
  damages: RoutingChainDamage[];
  /** Pickerate col palmare nel magazzino di arrivo della tappa */
  pickings: RoutingChainPicking[];
}

/**
 * Deviazione nella vista "Percorso": una o più tappe MISROUTING concatenate, disegnate su una
 * corsia parallela al percorso tra il punto da cui partono e quello in cui rientrano.
 */
export interface RoutingChainDetour {
  /** Colonne della griglia del percorso occupate dalla deviazione (dal punto sinistro al destro) */
  gridColumn: string;
  /** Corsia parallela: 0 è la più vicina al percorso */
  lane: number;
  /** True se la deviazione torna verso un punto precedente del percorso (disegnata da destra a sinistra) */
  backward: boolean;
  /** Il capo sinistro / destro coincide con un punto del percorso (raccordo verticale) */
  leftAnchored: boolean;
  rightAnchored: boolean;
  /** La partenza / l'arrivo coincide con un punto del percorso; altrimenti è un punto fuori percorso */
  startAnchored: boolean;
  endAnchored: boolean;
  startName: string;
  endName: string;
  /** Località (città e provincia) dei punti fuori percorso, se note */
  startPlace?: string;
  endPlace?: string;
  /** Codice del magazzino di partenza (vuoto se non è un magazzino) */
  startCode?: string;
  /** Gruppi di danno registrati nel punto di partenza fuori percorso (senza tappa in arrivo: nessun vettore) */
  startDamages: RoutingChainDamage[];
  /** Pickerate col palmare nel punto di partenza fuori percorso */
  startPickings: RoutingChainPicking[];
  legs: RoutingChainDetourLeg[];
}

/** Riga della tabella degli orari sotto la vista "Percorso": una per tappa */
export interface RoutingScheduleRow {
  /** Numero della tappa (parcelComplementarySequence); assente per le scansioni fuori percorso */
  sequence?: number;
  /** Nome del punto di partenza */
  from: string;
  /** Nome del punto di arrivo */
  to: string;
  /** Codici dei magazzini di partenza e arrivo (vuoti se il punto non è un magazzino) */
  fromCode?: string;
  toCode?: string;
  /** Gruppi di danno registrati nel magazzino della riga di transito (partenza e arrivo coincidono), mostrati sotto il nome */
  damages: RoutingChainDamage[];
  carrierName: string;
  status: string;
  /** Inizio della tappa (parcelComplementaryEventStart) */
  start?: string;
  /** Fine della tappa (parcelComplementaryEventEnd) */
  end?: string;
  /** True per le tappe MISROUTING (deviazioni) */
  misrouting: boolean;
}

/** Modalità di visualizzazione della tab Instradamento */
export type RoutingViewMode = 'chain' | 'map';

@Component({
  selector: 'app-parcel-detail-popup',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './parcel-detail-popup.component.html',
  styleUrl: './parcel-detail-popup.component.css'
})
export class ParcelDetailPopupComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() parcelCode: string = '';
  @Input() code: string = '';
  @Input() parcel: any = null; // Full parcel data for map
  @Input() waybill: any = null; // Waybill data for label generation
  @Input() dealer: any = null; // Dealer data for destination coordinates
  @Input() client: any = null;
  @Input() allParcels: any[] = []; // All parcels of the waybill for complete label generation
  @Input() clientsBrands: ClientsBrands[] = []; // ClientsBrands for API requests
  @Input() carriers: Carriers[] = []; // Anagrafica vettori: nome dell'azienda trasportatrice nella vista "Percorso"
  @Output() closed = new EventEmitter<void>();
  @Output() tabChanged = new EventEmitter<string>();
  @Output() openAxylogPopup = new EventEmitter<string[]>(); // Emette array di waybillCodes per aprire il popup Axylog
  @Output() openMailPopup = new EventEmitter<string[]>(); // Emette array di waybillCodes per aprire il popup Mail
  @Output() openInvalidateLoadingListsPopup = new EventEmitter<string[]>(); // Emette array di parcelCodes

  @ViewChild('barcodeCanvas', { static: false }) barcodeCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('qrcodeCanvas', { static: false }) qrcodeCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;
  @ViewChild('routingMapContainer', { static: false }) routingMapContainer!: ElementRef;

  // Ultima tab aperta: ricordata nel browser tra un'apertura e l'altra
  private static readonly ACTIVE_TAB_KEY = 'parcelDetailPopup.activeTab';
  private static readonly TABS = ['details', 'events', 'images', 'routing', 'barcode', 'map', 'functions'];
  activeTab: string = ParcelDetailPopupComponent.loadActiveTab();

  private static loadActiveTab(): string {
    try {
      const saved = localStorage.getItem(ParcelDetailPopupComponent.ACTIVE_TAB_KEY);
      if (saved && ParcelDetailPopupComponent.TABS.includes(saved)) {
        return saved;
      }
    } catch {
      // localStorage non disponibile: resta il default
    }
    return 'details';
  }

  codeType: 'barcode' | 'qrcode' = 'barcode';
  private map: L.Map | null = null;
  comParcels: any[] = []; // ComParcel data for routing tab
  warehouseMap: Map<string, any> = new Map(); // Map warehouse code to warehouse data
  loadingComParcels: boolean = false;
  // Modalità di visualizzazione dell'instradamento: ricordata nel browser tra un'apertura e l'altra
  private static readonly ROUTING_VIEW_MODE_KEY = 'parcelDetailPopup.routingViewMode';
  routingViewMode: RoutingViewMode = ParcelDetailPopupComponent.loadRoutingViewMode();

  private static loadRoutingViewMode(): RoutingViewMode {
    try {
      const saved = localStorage.getItem(ParcelDetailPopupComponent.ROUTING_VIEW_MODE_KEY);
      // Le viste rimosse (timeline, compatta) eventualmente salvate ricadono sul default
      if (saved === 'chain' || saved === 'map') {
        return saved;
      }
    } catch {
      // localStorage non disponibile: resta il default
    }
    return 'chain';
  }

  // Vista "Percorso" dell'instradamento: punti di transito alternati alle tratte col vettore
  routingChain: RoutingChainNode[] = [];
  // Deviazioni (tappe MISROUTING) disegnate su corsie parallele al percorso
  routingChainDetours: RoutingChainDetour[] = [];
  routingChainDetourLanes = 0;
  /** Altezza (px) delle corsie di deviazione: cresce se ci sono danni sui punti fuori percorso */
  routingChainDetourLaneHeight = ROUTING_DETOUR_LANE_HEIGHT;
  routingChainGridColumns = '';
  // Eventi di danno che non ricadono su un punto del percorso (magazzino assente o fuori percorso)
  routingChainUnplacedDamages: RoutingChainDamage[] = [];
  pickingEvents: Tracking[] = []; // Eventi di picking del collo (palmare), dalla risposta di com-parcels
  // Tabella degli orari sotto il percorso: inizio e fine di ogni tappa
  routingSchedule: RoutingScheduleRow[] = [];

  // Vista mappa dell'instradamento (Leaflet)
  private routingMap: L.Map | null = null;
  private routingMapTileLayer: L.TileLayer | null = null;
  private routingMapPointLayers: L.Layer[] = []; // Marker dei punti di transito con tooltip permanente
  private routingMapLegLayers: L.Layer[] = []; // Linee delle tratte con tooltip permanente
  routingMapPoints: RoutingMapPoint[] = [];
  routingMapLegs: RoutingMapLeg[] = [];
  // Magazzini fuori percorso in cui il collo è stato scansionato (misrouting): marker a sé, senza tratte
  routingMapOffRoutePoints: RoutingMapPoint[] = [];
  routingMapPointLabelsVisible: boolean = true;
  routingMapLegLabelsVisible: boolean = true;
  routingMapStyleId: RoutingMapStyleId = 'gray';

  /**
   * Basemap disponibili: di default una mappa grigia e desaturata.
   * Tutti gli stili usano le tile OpenStreetMap (nessuna API key richiesta);
   * Grigia, Chiara e Scura si ottengono con filtri CSS sul pannello delle tile.
   */
  readonly routingMapStyles: RoutingMapStyle[] = [
    {
      id: 'gray',
      label: 'Grigia',
      icon: 'fa-adjust',
      url: ROUTING_MAP_OSM_TILE_URL,
      attribution: ROUTING_MAP_OSM_ATTRIBUTION,
      cssClass: 'routing-map-gray'
    },
    {
      id: 'light',
      label: 'Chiara',
      icon: 'fa-sun',
      url: ROUTING_MAP_OSM_TILE_URL,
      attribution: ROUTING_MAP_OSM_ATTRIBUTION,
      cssClass: 'routing-map-light'
    },
    {
      id: 'street',
      label: 'Stradale',
      icon: 'fa-road',
      url: ROUTING_MAP_OSM_TILE_URL,
      attribution: ROUTING_MAP_OSM_ATTRIBUTION,
      cssClass: 'routing-map-street'
    },
    {
      id: 'dark',
      label: 'Scura',
      icon: 'fa-moon',
      url: ROUTING_MAP_OSM_TILE_URL,
      attribution: ROUTING_MAP_OSM_ATTRIBUTION,
      cssClass: 'routing-map-dark',
      isDark: true
    }
  ];

  // Eventi (ComParcelsEvent) tab properties
  parcelEvents: ComParcelEvent[] = [];
  isLoadingEvents: boolean = false;
  private eventsLoadedForCode: string | null = null;
  expandedEventNotes = new Set<number>(); // Indici degli eventi con nota espansa
  expandedDamageEvents = new Set<number>(); // Indici degli eventi danno con dettagli aperti (chiusi di default)
  readonly eventNotePreviewLength = 250; // Massimo caratteri nota prima del "Mostra di più"

  // Immagini (Opr_Images) tab properties
  images: Image[] = [];
  isLoadingImages: boolean = false;
  private imagesLoadedForCode: string | null = null;
  imagesViewMode: 'grid' | 'list' = 'list'; // Compatta (griglia) o una sotto l'altra (lista)
  previewImage: Image | null = null; // Immagine aperta in anteprima

  loadingListsPopupVisible = false;
  loadingListsPopupSubmitting = false;
  loadingListsPopupParcels: ParcelToInvalid[] = [];
  loadingListsPopupInitialTab: LoadingListsAction = 'invalidate';

  constructor(
    private labelGenerationService: LabelGenerationService,
    private notificationService: NotificationService,
    private waybillDocumentService: WaybillDocumentService,
    private shipmentManagementService: ShipmentManagementService
  ) {}

  get displayCode(): string {
    return this.code || this.parcelCode;
  }

  get barcodeValue(): string {
    // Use parcelBarcode for barcode/QR generation, fallback to displayCode
    return this.parcel?.parcelBarcode || this.displayCode;
  }

  ngOnInit(): void {
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset dei dati di routing, eventi e immagini quando cambia il parcel
    if (changes['parcel'] && !changes['parcel'].firstChange) {
      this.comParcels = [];
      this.pickingEvents = [];
      this.warehouseMap.clear();
      this.destroyRoutingMap();
      this.routingMapPoints = [];
      this.routingMapLegs = [];
      this.routingChain = [];
      this.routingSchedule = [];
      this.routingChainUnplacedDamages = [];
      this.routingChainDetours = [];
      this.parcelEvents = [];
      this.eventsLoadedForCode = null;
      this.expandedEventNotes.clear();
      this.expandedDamageEvents.clear();
      this.images = [];
      this.imagesLoadedForCode = null;
      this.previewImage = null;
    }

    // Quando il popup diventa visibile o cambiano i dati, aggiorna il contenuto della tab attiva
    if ((changes['visible'] && changes['visible'].currentValue === true) || changes['parcel']) {
      if (this.visible) {
        if (this.activeTab === 'barcode') {
          setTimeout(() => this.generateCode(), 100);
        } else if (this.activeTab === 'map' && this.hasValidCoordinates()) {
          setTimeout(() => this.initializeMap(), 100);
        } else if (this.activeTab === 'routing') {
          // Ricarica i dati di instradamento quando cambia il parcel
          this.loadRouting();
        } else if (this.activeTab === 'events') {
          this.loadEvents();
        } else if (this.activeTab === 'images') {
          this.loadImages();
        }
      }
    }
    
    // Dealer di destinazione e nomi dei vettori entrano nella vista "Percorso": la riallinea
    if ((changes['dealer'] || changes['carriers']) && this.comParcels.length > 0) {
      this.buildRoutingChain();
    }

    // Aggiorna anche quando cambiano code/parcelCode per barcode
    if ((changes['code'] || changes['parcelCode']) && this.visible && this.activeTab === 'barcode') {
      setTimeout(() => this.generateCode(), 100);
    }
    
    // Aggiorna la mappa quando cambiano dealer o parcel
    if ((changes['dealer'] || changes['parcel']) && this.visible && this.activeTab === 'map' && this.hasValidCoordinates()) {
      setTimeout(() => this.initializeMap(), 100);
    }
  }

  ngAfterViewInit(): void {
    if (this.activeTab === 'barcode') {
      setTimeout(() => this.generateCode(), 100);
    } else if (this.activeTab === 'map' && this.hasValidCoordinates()) {
      setTimeout(() => this.initializeMap(), 100);
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    this.destroyRoutingMap();
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.visible) {
      // Se è aperta l'anteprima immagine, chiudi prima quella
      if (this.previewImage) {
        this.closeImagePreview();
        return;
      }
      this.close();
    }
  }

  close(): void {
    this.closed.emit();
  }

  copyToClipboard(text: string): void {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        console.log('Text copied to clipboard:', text);
      }).catch(err => {
        console.error('Failed to copy text: ', err);
      });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        console.log('Text copied to clipboard:', text);
      } catch (err) {
        console.error('Fallback: Unable to copy', err);
      }
      document.body.removeChild(textArea);
    }
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.tabChanged.emit(tab);
    try {
      localStorage.setItem(ParcelDetailPopupComponent.ACTIVE_TAB_KEY, tab);
    } catch {
      // localStorage non disponibile: la scelta vale solo per questa sessione
    }

    // Uscendo dalla tab Instradamento il contenitore viene rimosso dal DOM
    if (tab !== 'routing') {
      this.destroyRoutingMap();
    }

    if (tab === 'barcode') {
      setTimeout(() => this.generateCode(), 100);
    } else if (tab === 'map' && this.hasValidCoordinates()) {
      setTimeout(() => this.initializeMap(), 100);
    } else if (tab === 'routing') {
      this.loadRouting();
    } else if (tab === 'events') {
      this.loadEvents();
    } else if (tab === 'images') {
      this.loadImages();
    }
  }

  /**
   * Recupera le immagini del collo (Opr_Images) dall'endpoint /parcel-images
   */
  loadImages(): void {
    const code = this.parcel?.parcelCode || this.displayCode;
    if (!code) {
      this.notificationService.error('Nessun codice collo disponibile');
      return;
    }

    // Se le immagini sono già state caricate per questo collo, non ricaricarle
    if (this.imagesLoadedForCode === code && !this.isLoadingImages) {
      return;
    }

    this.isLoadingImages = true;

    this.shipmentManagementService.getParcelImages(code)
      .subscribe({
        next: (images) => {
          // Ordina dalla più recente alla più vecchia
          this.images = (images || []).sort((a, b) =>
            new Date(b.imageCreatedAt || 0).getTime() - new Date(a.imageCreatedAt || 0).getTime());
          this.imagesLoadedForCode = code;
          this.isLoadingImages = false;
        },
        error: (error) => {
          console.error('Error loading parcel images:', error);
          this.notificationService.error('Errore nel caricamento delle immagini del collo');
          this.images = [];
          this.isLoadingImages = false;
        }
      });
  }

  /**
   * Etichetta italiana del tipo immagine (fallback: il tipo grezzo)
   */
  getImageTypeLabel(image: Image): string {
    const type = (image.imageType || '').toUpperCase();
    return IMAGE_TYPE_LABELS[type] || image.imageType || 'Immagine';
  }

  /**
   * True se il contenuto è un PDF (non visualizzabile come <img>)
   */
  isPdf(image: Image): boolean {
    return (image.imageMimeType || '').toLowerCase() === 'application/pdf'
      || (image.imageExtension || '').toLowerCase().includes('pdf')
      || (image.imageType || '').toUpperCase() === 'POD_PDF';
  }

  /**
   * Contenuto Base64 dell'immagine, ripulito da eventuali spazi/newline
   */
  private getImageBase64(image: Image): string | null {
    const raw = image.imageContentBase64 || image.imageContentByte;
    return raw ? raw.replace(/\s/g, '') : null;
  }

  /**
   * Data URL per visualizzare l'immagine in un tag <img> (null per PDF o senza contenuto)
   */
  getImageSrc(image: Image): string | null {
    if (this.isPdf(image)) {
      return null;
    }
    const base64 = this.getImageBase64(image);
    if (!base64) {
      return null;
    }
    return `data:${image.imageMimeType || 'image/jpeg'};base64,${base64}`;
  }

  /**
   * Dimensione file leggibile (B/KB/MB)
   */
  formatImageSize(bytes: number | undefined): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Converte una stringa Base64 in Blob
   */
  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], {type: mimeType});
  }

  /**
   * Apre l'anteprima dell'immagine (i PDF si aprono in una nuova scheda)
   */
  openImagePreview(image: Image): void {
    if (this.isPdf(image)) {
      const base64 = this.getImageBase64(image);
      if (!base64) {
        this.notificationService.error('Contenuto del documento non disponibile');
        return;
      }
      try {
        const blob = this.base64ToBlob(base64, 'application/pdf');
        window.open(URL.createObjectURL(blob), '_blank');
      } catch (error) {
        console.error('Error opening PDF:', error);
        this.notificationService.error('Errore nell\'apertura del documento');
      }
      return;
    }

    if (!this.getImageSrc(image)) {
      this.notificationService.warning('Contenuto dell\'immagine non disponibile');
      return;
    }
    this.previewImage = image;
  }

  /**
   * Chiude l'anteprima dell'immagine
   */
  closeImagePreview(): void {
    this.previewImage = null;
  }

  /**
   * Scarica l'immagine come file
   */
  downloadImage(image: Image): void {
    const base64 = this.getImageBase64(image);
    if (!base64) {
      this.notificationService.error('Contenuto dell\'immagine non disponibile');
      return;
    }

    try {
      const mimeType = image.imageMimeType || 'application/octet-stream';
      const blob = this.base64ToBlob(base64, mimeType);

      // Nome file: parcelCode_tipo_timestamp.ext
      const code = image.imageParcelCode || this.parcel?.parcelCode || this.displayCode || 'parcel';
      const type = (image.imageType || 'IMG').toUpperCase();
      const timestamp = new Date().toISOString()
        .replace(/[:.]/g, '-')
        .substring(0, 19);
      const extension = (image.imageExtension || '').replace('.', '')
        || (mimeType.includes('/') ? mimeType.split('/').pop() : '') || '';
      const fileName = `${code}_${type}_${timestamp}${extension ? '.' + extension : ''}`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
      this.notificationService.error('Errore nel download dell\'immagine');
    }
  }

  /**
   * I nomi dei warehouse arrivano insieme ai dati di instradamento: se la tab Instradamento
   * non è stata ancora aperta, li recupera in silenzio così la tab Eventi mostra il nome
   * e non il codice. Non tocca i dati di instradamento né mostra notifiche.
   */
  private ensureWarehousesLoaded(): void {
    const parcelCode = this.parcel?.parcelCode;
    if (!parcelCode || this.warehouseMap.size > 0) {
      return;
    }

    this.shipmentManagementService.getComParcelsByParcelCode(parcelCode, this.clientsBrands)
      .subscribe({
        next: (response) => {
          if (this.parcel?.parcelCode !== parcelCode) {
            return; // il collo è cambiato nel frattempo
          }
          (response?.comWarehouses || []).forEach(warehouse => {
            this.warehouseMap.set(warehouse.warehouseCode, warehouse);
          });
        },
        error: () => { /* silenzioso: resta il codice del warehouse */ }
      });
  }

  /**
   * Recupera gli eventi complementari del collo (ComParcelsEvent)
   * dall'endpoint /parcel-event
   */
  loadEvents(): void {
    const parcelCode = this.parcel?.parcelCode || this.displayCode;
    const parcelWaybillCode = this.parcel?.parcelWaybillCode;
    const parcelShipmentCode = this.parcel?.parcelShipmentCode;

    if (!parcelCode || !parcelWaybillCode || !parcelShipmentCode) {
      this.notificationService.error('Codice collo, bolla o spedizione non disponibile');
      return;
    }

    // Se gli eventi sono già stati caricati per questo collo, non ricaricarli
    if (this.eventsLoadedForCode === parcelCode && !this.isLoadingEvents) {
      return;
    }

    this.isLoadingEvents = true;
    this.ensureWarehousesLoaded();

    this.shipmentManagementService.getParcelEvents(parcelCode, parcelWaybillCode, parcelShipmentCode)
      .subscribe({
        next: (events) => {
          // Ordina dal più recente al più vecchio (l'evento più nuovo in cima alla timeline)
          this.parcelEvents = (events || []).sort((a, b) =>
            new Date(b.parcelEventComplementaryCreatedAt || 0).getTime() - new Date(a.parcelEventComplementaryCreatedAt || 0).getTime());
          this.eventsLoadedForCode = parcelCode;
          this.expandedEventNotes.clear();
          this.expandedDamageEvents.clear();
          this.isLoadingEvents = false;

          // La vista "Percorso" dell'instradamento riporta gli eventi di danno: la riallinea
          if (this.comParcels.length > 0) {
            this.buildRoutingChain();
          }
        },
        error: (error) => {
          console.error('Error loading parcel events:', error);
          this.notificationService.error('Errore nel caricamento degli eventi del collo');
          this.parcelEvents = [];
          this.isLoadingEvents = false;
        }
      });
  }

  /**
   * Data dell'ultimo evento del collo (il più recente è in cima alla lista)
   */
  get lastEventDate(): string | undefined {
    return this.parcelEvents.length > 0
      ? this.parcelEvents[0].parcelEventComplementaryCreatedAt
      : undefined;
  }

  /**
   * Transcodifica del nome evento nel relativo modello di presentazione
   * (etichetta italiana, icona, colore). Null se il nome non è mappato.
   */
  private getEventPresentation(event: ComParcelEvent): WaybillEventPresentation | null {
    const name = (event.parcelEventComplementaryName || '').trim().toUpperCase();
    return PARCEL_EVENT_PRESENTATIONS[name] || null;
  }

  /**
   * Etichetta dell'evento: transcodifica italiana se mappata,
   * altrimenti il nome grezzo (fallback: tipo o 'Evento')
   */
  getEventLabel(event: ComParcelEvent): string {
    const presentation = this.getEventPresentation(event);
    return presentation?.label || event.parcelEventComplementaryName || event.parcelEventComplementaryType || 'Evento';
  }

  /**
   * Icona FontAwesome per un evento: dalla transcodifica se mappata,
   * altrimenti euristica su tipo/nome
   */
  getEventIcon(event: ComParcelEvent): string {
    const presentation = this.getEventPresentation(event);
    if (presentation) {
      return presentation.icon;
    }

    const type = `${event.parcelEventComplementaryType || ''} ${event.parcelEventComplementaryName || ''}`.toUpperCase();
    if (type.includes('DELIVER') || type.includes('CONSEGN')) return 'fa-check-circle';
    if (type.includes('MAIL') || type.includes('EMAIL')) return 'fa-envelope';
    if (type.includes('AXYLOG')) return 'fa-cloud-upload-alt';
    if (type.includes('PDF') || type.includes('PRINT') || type.includes('STAMPA')) return 'fa-file-pdf';
    if (type.includes('CARRIER') || type.includes('VETTORE') || type.includes('TRANSPORT')) return 'fa-truck';
    if (type.includes('STATUS') || type.includes('STATO')) return 'fa-exchange-alt';
    if (type.includes('NOTE') || type.includes('NOTA')) return 'fa-sticky-note';
    return 'fa-history';
  }

  /**
   * Classe colore del marker della timeline: variante dalla transcodifica
   * se mappata, altrimenti classe derivata dallo stato
   */
  getEventVariantClass(event: ComParcelEvent): string {
    const presentation = this.getEventPresentation(event);
    return presentation ? `event-variant--${presentation.variant}` : this.getEventStatusClass(event);
  }

  /**
   * Classe CSS di stato per un evento
   */
  getEventStatusClass(event: ComParcelEvent): string {
    const status = (event.parcelEventComplementaryStatus || '').toUpperCase();
    if (['OK', 'COMPLETED', 'EXECUTED', 'SUCCESS', 'DELIVERED', 'ACTIVE'].includes(status)) return 'status-executed';
    if (['ERROR', 'FAILED', 'KO'].includes(status)) return 'status-failed';
    return 'status-in-progress';
  }

  /** True per l'evento "Danneggiato" (DAMAGE), che ha una veste dedicata */
  isDamageEvent(event: ComParcelEvent): boolean {
    return (event.parcelEventComplementaryName || '').trim().toUpperCase() === 'DAMAGE';
  }

  /** Collo segnalato come danneggiato: parcelIsDamaged = true oppure 'Y' */
  isParcelFlaggedDamaged(): boolean {
    const flag = this.parcel?.parcelIsDamaged;
    return flag === true || (typeof flag === 'string' && flag.trim().toUpperCase() === 'Y');
  }

  /**
   * True se il collo è segnalato come danneggiato ma tra gli eventi (già caricati) non c'è
   * nessun evento DAMAGE: il danno non è tracciato e va indicato.
   * Prima del caricamento degli eventi resta false, per non segnalare un'assenza non verificata.
   */
  isDamagedWithoutEvent(): boolean {
    const parcelCode = this.parcel?.parcelCode || this.displayCode;
    return this.isParcelFlaggedDamaged()
      && !this.isLoadingEvents
      && this.eventsLoadedForCode === parcelCode
      && !this.parcelEvents.some(event => this.isDamageEvent(event));
  }

  /** True se l'evento danno ha note o valori da mostrare nel pannello dettagli */
  hasDamageDetails(event: ComParcelEvent): boolean {
    return !!(event.parcelEventComplementaryValueClob || this.getEventValue(event));
  }

  isDamageDetailsExpanded(index: number): boolean {
    return this.expandedDamageEvents.has(index);
  }

  toggleDamageDetails(index: number): void {
    if (this.expandedDamageEvents.has(index)) {
      this.expandedDamageEvents.delete(index);
    } else {
      this.expandedDamageEvents.add(index);
    }
  }

  /**
   * Se la nota dell'evento danno è un JSON a oggetto, la restituisce come righe
   * etichetta/valore leggibili; altrimenti null (verrà mostrata come testo).
   */
  getDamageDetailRows(event: ComParcelEvent): { label: string; value: string }[] | null {
    const raw = (event.parcelEventComplementaryValueClob || '').trim();
    if (!raw.startsWith('{')) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return null;
      }
      return Object.entries(parsed)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => ({
          label: k.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim(),
          value: typeof v === 'object' ? JSON.stringify(v) : String(v)
        }));
    } catch {
      return null;
    }
  }

  /**
   * True se la nota dell'evento supera la lunghezza di anteprima
   */
  isEventNoteTruncatable(event: ComParcelEvent): boolean {
    return (event.parcelEventComplementaryValueClob || '').length > this.eventNotePreviewLength;
  }

  /**
   * True se la nota dell'evento è espansa
   */
  isEventNoteExpanded(index: number): boolean {
    return this.expandedEventNotes.has(index);
  }

  /**
   * Espande/comprime la nota dell'evento
   */
  toggleEventNote(index: number): void {
    if (this.expandedEventNotes.has(index)) {
      this.expandedEventNotes.delete(index);
    } else {
      this.expandedEventNotes.add(index);
    }
  }

  /**
   * Nota dell'evento: completa se breve o espansa, altrimenti
   * troncata alla lunghezza di anteprima
   */
  getEventNote(event: ComParcelEvent, index: number): string {
    const note = event.parcelEventComplementaryValueClob || '';
    if (note.length <= this.eventNotePreviewLength || this.isEventNoteExpanded(index)) {
      return note;
    }
    return note.substring(0, this.eventNotePreviewLength) + '…';
  }

  /**
   * Valore dell'evento: testo, numero o data (il primo valorizzato)
   */
  getEventValue(event: ComParcelEvent): string | null {
    if (event.parcelEventComplementaryValueText) {
      return event.parcelEventComplementaryValueText;
    }
    if (event.parcelEventComplementaryValueNumber !== null && event.parcelEventComplementaryValueNumber !== undefined) {
      return String(event.parcelEventComplementaryValueNumber);
    }
    if (event.parcelEventComplementaryValueDate) {
      const date = new Date(event.parcelEventComplementaryValueDate);
      return isNaN(date.getTime()) ? event.parcelEventComplementaryValueDate : date.toLocaleString('it-IT');
    }
    return null;
  }

  setCodeType(type: 'barcode' | 'qrcode'): void {
    this.codeType = type;
    setTimeout(() => this.generateCode(), 100);
  }

  private generateCode(): void {
    if (!this.barcodeValue) return;

    if (this.codeType === 'barcode' && this.barcodeCanvas) {
      try {
        JsBarcode(this.barcodeCanvas.nativeElement, this.barcodeValue, {
          format: 'CODE128',
          width: 2,
          height: 100,
          displayValue: true,
          fontSize: 14,
          margin: 10
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    } else if (this.codeType === 'qrcode' && this.qrcodeCanvas) {
      try {
        QRCode.toCanvas(this.qrcodeCanvas.nativeElement, this.barcodeValue, {
          width: 200,
          margin: 2,
          errorCorrectionLevel: 'M'
        });
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    }
  }

  hasValidCoordinates(): boolean {
    // Check if dealer has coordinates (minimum requirement to show map)
    return !!(this.dealer?.dealerLatitude && this.dealer?.dealerLongitude);
  }

  isDelivered(): boolean {
    const deliveredStatuses = ['DELIVERED'];
    return deliveredStatuses.includes(this.parcel?.parcelDeliveryStatus?.toUpperCase() || '');
  }

  isDelayed(): boolean {
    // Check if delivery is delayed (no delivery date AND current date is past estimated delivery date)
    if (this.parcel?.parcelDeliveryDate) {
      return false; // Already delivered, not delayed
    }
    
    if (!this.parcel?.parcelEstimatedDeliveryDate) {
      return false; // No estimated date to compare
    }
    
    const currentDate = new Date();
    const estimatedDate = new Date(this.parcel.parcelEstimatedDeliveryDate);
    
    return currentDate > estimatedDate;
  }

  /**
   * Verifica se il collo ha coordinate di consegna
   * @returns true se ci sono coordinate, false altrimenti
   */
  hasDeliveryCoordinates(): boolean {
    return !!(this.parcel?.parcelLatitude && this.parcel?.parcelLongitude);
  }

  /**
   * Calcola la distanza in metri tra due coordinate usando la formula di Haversine
   * @param lat1 Latitudine punto 1
   * @param lon1 Longitudine punto 1
   * @param lat2 Latitudine punto 2
   * @param lon2 Longitudine punto 2
   * @returns Distanza in metri
   */
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Raggio della Terra in metri
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distanza in metri
  }

  /**
   * Verifica se la consegna è avvenuta entro il raggio di CIENT_MAXIMUM_RANGE dal destinatario
   * @returns true se entro CIENT_MAXIMUM_RANGE (in metri), false altrimenti
   */
  isDeliveryWithinRadius(): boolean {
    const dealerLat = this.dealer?.dealerLatitude;
    const dealerLng = this.dealer?.dealerLongitude;
    const parcelLat = this.parcel?.parcelLatitude;
    const parcelLng = this.parcel?.parcelLongitude;

    if (!dealerLat || !dealerLng || !parcelLat || !parcelLng) {
      return false;
    }

    const distance = this.calculateDistance(dealerLat, dealerLng, parcelLat, parcelLng);
    return distance <= this.client.clientMaximumRange;
  }

  /**
   * Ottiene la distanza dalla destinazione in metri
   * @returns Distanza in metri o null se non disponibile
   */
  getDistanceFromDestination(): number | null {
    const dealerLat = this.dealer?.dealerLatitude;
    const dealerLng = this.dealer?.dealerLongitude;
    const parcelLat = this.parcel?.parcelLatitude;
    const parcelLng = this.parcel?.parcelLongitude;

    if (!dealerLat || !dealerLng || !parcelLat || !parcelLng) {
      return null;
    }

    return this.calculateDistance(dealerLat, dealerLng, parcelLat, parcelLng);
  }

  private initializeMap(): void {
    if (!this.mapContainer) {
      return;
    }

    // Require at least dealer coordinates
    const dealerLat = this.dealer?.dealerLatitude;
    const dealerLng = this.dealer?.dealerLongitude;
    
    if (!dealerLat || !dealerLng) {
      console.warn('Cannot initialize map: dealer coordinates missing');
      return;
    }

    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    // Prepare parcel coordinates (optional)
    const parcelLat = this.parcel?.parcelLatitude;
    const parcelLng = this.parcel?.parcelLongitude;

    // Determine map center
    let centerLat = dealerLat;
    let centerLng = dealerLng;
    let zoomLevel = 16;

    // If both exist, center between them
    if (parcelLat && parcelLng) {
      centerLat = (dealerLat + parcelLat) / 2;
      centerLng = (dealerLng + parcelLng) / 2;
      zoomLevel = 15;
    }

    this.map = L.map(this.mapContainer.nativeElement, {
      center: [centerLat, centerLng],
      zoom: zoomLevel,
      zoomControl: true,
      attributionControl: true
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map);

    // Add dealer marker (destination) if available
    if (dealerLat && dealerLng) {
      const dealerIcon = L.icon({
        iconUrl: 'assets/img/marker-custom.png',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });

      const dealerMarker = L.marker([dealerLat, dealerLng], { icon: dealerIcon }).addTo(this.map);
      
      const dealerPopup = `
        <div class="map-popup">
          <strong>📍 Destinazione (Dealer)</strong><br>
          ${this.dealer?.dealerName || this.dealer?.dealerLegalName || 'Dealer'}<br>
          <small>Lat: ${dealerLat}, Lng: ${dealerLng}</small>
        </div>
      `;
      
      dealerMarker.bindPopup(dealerPopup);

      // Add this.client.clientMaximumRange m radius circle around dealer
      L.circle([dealerLat, dealerLng], {
        color: '#007bff',
        fillColor: '#007bff',
        fillOpacity: 0.1,
        radius: this.client.clientMaximumRange
      }).addTo(this.map).bindPopup(`Raggio di consegna: ${this.client.clientMaximumRange} m`);
    }

    // Add parcel marker (current location) if available
    if (parcelLat && parcelLng) {
      const parcelIcon = L.icon({
        iconUrl: 'assets/img/marker-custom.png',
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -28],
        className: 'parcel-marker'
      });

      const parcelMarker = L.marker([parcelLat, parcelLng], { icon: parcelIcon }).addTo(this.map);
      
      const parcelPopup = `
        <div class="map-popup">
          <strong>📦 Posizione Consegna Collo</strong><br>
          Collo: ${this.displayCode}<br>
          <small>Lat: ${parcelLat}, Lng: ${parcelLng}</small>
        </div>
      `;
      
      parcelMarker.bindPopup(parcelPopup).openPopup();
    }

    // Fit bounds to ensure the this.client.clientMaximumRange m radius is always fully visible
    if (dealerLat && dealerLng) {
      // Calculate the bounds that include the this.client.clientMaximumRange m radius circle
      // Convert this.client.clientMaximumRange m to degrees (approximately 0.009 degrees at equator)
      const radiusInDegrees = this.client.clientMaximumRange / 111320; // 111320 meters per degree
      
      const bounds = L.latLngBounds([
        [dealerLat - radiusInDegrees, dealerLng - radiusInDegrees],
        [dealerLat + radiusInDegrees, dealerLng + radiusInDegrees]
      ]);
      
      // If parcel marker exists, extend bounds to include it
      if (parcelLat && parcelLng) {
        bounds.extend([parcelLat, parcelLng]);
      }
      
      this.map.fitBounds(bounds, { padding: [50, 50] });
    }

    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 100);
  }

  // Funzionalità methods
  /**
   * Stampa l'etichetta del singolo collo corrente
   */
  printLabel(): void {
    if (!this.parcel) {
      this.notificationService.error('Nessun collo selezionato');
      return;
    }

    // Crea un array con il singolo collo
    const parcels = [this.parcel];
    
    // Genera l'etichetta usando il servizio
    this.labelGenerationService.generateWaybillLabels(parcels, this.waybill, this.dealer)
      .subscribe({
        next: (success) => {
          if (success) {
            this.notificationService.success(`Etichetta generata per collo ${this.parcel.parcelCode || this.displayCode}`);
          } else {
            this.notificationService.error('Errore nella generazione dell\'etichetta');
          }
        },
        error: (error) => {
          console.error('Error generating label:', error);
          this.notificationService.error('Errore nella generazione dell\'etichetta');
        }
      });
  }

  /**
   * Stampa tutte le etichette della bolla (tutti i colli)
   */
  printAllLabels(): void {
    if (!this.waybill) {
      this.notificationService.error('Nessuna bolla disponibile');
      return;
    }

    // Usa tutti i colli della bolla se disponibili, altrimenti solo il collo corrente
    const parcelsToGenerate = this.allParcels && this.allParcels.length > 0 ? this.allParcels : [this.parcel];
    
    if (!parcelsToGenerate || parcelsToGenerate.length === 0) {
      this.notificationService.error('Nessun collo disponibile per la bolla');
      return;
    }

    // Genera tutte le etichette della bolla
    this.labelGenerationService.generateWaybillLabels(parcelsToGenerate, this.waybill, this.dealer)
      .subscribe({
        next: (success) => {
          if (success) {
            const count = parcelsToGenerate.length;
            const message = count === 1 
              ? `Etichetta generata per bolla ${this.waybill.waybillCode}`
              : `Etichette generate per bolla ${this.waybill.waybillCode} (${count} colli)`;
            this.notificationService.success(message);
          } else {
            this.notificationService.error('Errore nella generazione delle etichette');
          }
        },
        error: (error) => {
          console.error('Error generating labels:', error);
          this.notificationService.error('Errore nella generazione delle etichette');
        }
      });
  }

  /**
   * Stampa il documento della bolla (PDF completo)
   */
  printWaybill(): void {
    if (!this.waybill) {
      this.notificationService.error('Nessuna bolla disponibile');
      return;
    }

    if (!this.waybill.waybillCode) {
      this.notificationService.error('Codice bolla non disponibile');
      return;
    }

    // Usa il servizio shipmentManagementService per generare il PDF
    this.shipmentManagementService.printWaybills([{ key: this.waybill.waybillCode, value: this.waybill.waybillShipmentCode }])
      .subscribe({
        next: (blob) => {
          this.notificationService.success(`PDF della bolla ${this.waybill.waybillCode} generato con successo`);
        },
        error: (error) => {
          console.error('Error generating waybill PDF:', error);
          this.notificationService.error('Errore nella generazione del PDF della bolla');
        }
      });
  }

  /**
   * Apre il popup Axylog per inviare la bolla ad Axylog
   */
  sendToAxylog(): void {
    if (!this.waybill || !this.waybill.waybillCode) {
      this.notificationService.error('Nessuna bolla disponibile per l\'invio in Axylog');
      return;
    }

    // Emetti l'evento per aprire il popup Axylog nel componente padre
    this.openAxylogPopup.emit([this.waybill.waybillCode]);
  }

  /**
   * Apre il popup Mail per inviare la bolla via email
   */
  sendByEmail(): void {
    if (!this.waybill || !this.waybill.waybillCode) {
      this.notificationService.error('Nessuna bolla disponibile per l\'invio via email');
      return;
    }

    // Emetti l'evento per aprire il popup Mail nel componente padre
    this.openMailPopup.emit([this.waybill.waybillCode]);
  }

  /**
   * Recupera e mostra i dati di instradamento (ComParcel) per il collo corrente
   */
  loadRouting(): void {
    if (!this.parcel || !this.parcel.parcelCode) {
      this.notificationService.error('Nessun codice collo disponibile');
      return;
    }

    // Se i dati sono già stati caricati per questo parcel, non ricaricarli
    if (this.comParcels.length > 0 && !this.loadingComParcels) {
      this.refreshRoutingMapIfActive();
      this.loadRoutingChainEvents();
      return;
    }

    this.loadingComParcels = true;

    this.shipmentManagementService.getComParcelsByParcelCode(this.parcel.parcelCode, this.clientsBrands)
      .subscribe({
        next: (response) => {
          if (response && response.comParcels && response.comParcels.length > 0) {
            // Ordina per sequenza numerica. Le scansioni fuori percorso (misrouting) non hanno sequenza:
            // restano dopo la tappa che le precede nella risposta (il backend le inserisce in ordine di data)
            const sortKeys = new Map<any, number>();
            let lastSequence = 0;
            response.comParcels.forEach(comParcel => {
              if (!this.isOffRouteScan(comParcel)) {
                lastSequence = comParcel.parcelComplementarySequence || 0;
              }
              sortKeys.set(comParcel, lastSequence);
            });
            this.comParcels = response.comParcels.sort((a, b) => sortKeys.get(a)! - sortKeys.get(b)!);
            this.pickingEvents = response.pickingEvents || [];

            // Crea mappa dei warehouse per lookup rapido
            this.warehouseMap.clear();
            if (response.comWarehouses && response.comWarehouses.length > 0) {
              response.comWarehouses.forEach(warehouse => {
                this.warehouseMap.set(warehouse.warehouseCode, warehouse);
              });
            }
            
            console.log('ComParcels loaded and sorted:', this.comParcels);
            console.log('Warehouses map created:', this.warehouseMap);

            this.buildRoutingChain();
            this.loadRoutingChainEvents();

            // Se la vista mappa è attiva, ricostruisce punti e tratte georeferenziate
            this.refreshRoutingMapIfActive();
          } else {
            this.comParcels = [];
            this.warehouseMap.clear();
            this.routingMapPoints = [];
            this.routingMapLegs = [];
            this.routingChain = [];
            this.routingSchedule = [];
            this.routingChainUnplacedDamages = [];
            this.routingChainDetours = [];
            this.notificationService.warning('Nessun dato di instradamento trovato per questo collo');
          }
          this.loadingComParcels = false;
        },
        error: (error) => {
          console.error('Error loading ComParcels:', error);
          this.notificationService.error('Errore nel caricamento dei dati di instradamento');
          this.loadingComParcels = false;
          this.comParcels = [];
          this.warehouseMap.clear();
          this.routingChain = [];
          this.routingSchedule = [];
          this.routingChainUnplacedDamages = [];
          this.routingChainDetours = [];
        }
      });
  }

  /**
   * Get warehouse name by warehouse code
   * @param warehouseCode The warehouse code to lookup
   * @returns Warehouse name or the code if not found
   */
  getWarehouseName(warehouseCode: string | undefined): string {
    if (!warehouseCode) return '-';

    const warehouse = this.warehouseMap.get(warehouseCode);
    return warehouse?.warehouseName || warehouse?.warehouseShortName || warehouseCode;
  }

  /**
   * Cambia la modalità di visualizzazione dell'instradamento.
   * Sulla vista mappa ricostruisce i dati georeferenziati e inizializza Leaflet,
   * sulle altre viste distrugge la mappa per liberare le risorse.
   */
  setRoutingViewMode(mode: RoutingViewMode): void {
    this.routingViewMode = mode;
    try {
      localStorage.setItem(ParcelDetailPopupComponent.ROUTING_VIEW_MODE_KEY, mode);
    } catch {
      // localStorage non disponibile: la scelta vale solo per questa sessione
    }

    if (mode === 'map') {
      this.buildRoutingGeoData();
      setTimeout(() => this.initializeRoutingMap(), 100);
    } else {
      this.destroyRoutingMap();
    }

    this.loadRoutingChainEvents();
  }

  /**
   * Costruisce la vista "Percorso" (O----T----O) a partire dai ComParcels: i punti di transito (O)
   * si alternano alle tratte (T), ognuna con la sua azienda trasportatrice.
   * A differenza della mappa non servono le coordinate: entrano tutti i punti delle tappe.
   */
  private buildRoutingChain(): void {
    // Le tappe MISROUTING non fanno parte del percorso: corrono parallele ad esso (deviazioni).
    // Senza un percorso su cui affiancarle (meno di due punti) restano tappe come le altre.
    let mainLegs = this.comParcels.filter(comParcel => !this.isMisroutingLeg(comParcel));
    let chain = this.buildRoutingChainNodes(mainLegs);
    if (chain.nodes.length < 2 && mainLegs.length < this.comParcels.length) {
      mainLegs = this.comParcels;
      chain = this.buildRoutingChainNodes(mainLegs);
    }
    const nodes = chain.nodes;

    const schedule: RoutingScheduleRow[] = [];
    // Deviazioni ancora da collocare: tappe MISROUTING consecutive e concatenate (l'arrivo di una è la partenza della successiva)
    const openDetours: {
      start: RoutingChainPoint | null;
      end: RoutingChainPoint | null;
      legs: RoutingChainDetourLeg[];
      /** Ultimo punto del percorso raggiunto quando la deviazione comincia */
      reachedIndex: number;
      /** Punto del percorso da cui parte la deviazione, quando è già noto (scansioni fuori percorso) */
      startIndex?: number;
    }[] = [];
    let reachedIndex = 0;
    let continuesDetour = false;

    this.comParcels.forEach((comParcel, index) => {
      const status = comParcel.parcelComplementaryStatus || '';
      const sequence = comParcel.parcelComplementarySequence || (index + 1);
      const carrierName = this.getCarrierName(this.resolveLegCarrierCode(comParcel));
      const ends = chain.ends.get(comParcel);
      let fromName: string;
      let toName: string;
      let fromPoint: RoutingChainPoint | null;
      let toPoint: RoutingChainPoint | null;

      if (ends) {
        fromName = ends.from.point.name;
        toName = ends.to.point.name;
        fromPoint = ends.from.point;
        toPoint = ends.to.point;
        reachedIndex = nodes.indexOf(ends.to);
        continuesDetour = false;
      } else {
        const from = this.resolveRoutingChainPoint(comParcel, 'from', false);
        const to = this.resolveRoutingChainPoint(comParcel, 'to', false);
        fromName = from?.name || 'Non definito';
        toName = to?.name || 'Non definito';
        fromPoint = from;
        toPoint = to;

        // Scansione fuori percorso: non è una tappa numerata e la deviazione parte
        // dall'ultimo punto raggiunto sul percorso
        const offRouteScan = this.isOffRouteScan(comParcel);
        const reachedPoint = nodes[reachedIndex].point;

        const leg: RoutingChainDetourLeg = {
          to: toName,
          toPlace: to?.place,
          toCode: to?.kind === 'warehouse' ? to.code : undefined,
          damages: [],
          pickings: [],
          carrierName,
          title: [
            offRouteScan
              ? `Misrouting · ${reachedPoint.name} → ${toName}`
              : `Misrouting · Tappa ${sequence}: ${fromName} → ${toName}`,
            carrierName,
            offRouteScan ? '' : this.getRoutingStatusLabel(status)
          ].filter(part => !!part).join(' · ')
        };
        const previous = openDetours[openDetours.length - 1];
        if (offRouteScan) {
          openDetours.push({ start: reachedPoint, end: to, legs: [leg], reachedIndex, startIndex: reachedIndex });
        } else if (continuesDetour && previous && this.isSameRoutingPoint(previous.end, from)) {
          previous.legs.push(leg);
          previous.end = to;
        } else {
          openDetours.push({ start: from, end: to, legs: [leg], reachedIndex });
        }
        continuesDetour = true;
      }

      // La tabella degli orari ha una riga per ogni tappa, soste e deviazioni comprese
      schedule.push({
        sequence: this.isOffRouteScan(comParcel) ? undefined : sequence,
        from: fromName,
        to: toName,
        fromCode: fromPoint?.kind === 'warehouse' ? fromPoint.code : undefined,
        toCode: toPoint?.kind === 'warehouse' ? toPoint.code : undefined,
        damages: [],
        carrierName,
        status,
        start: comParcel.parcelComplementaryEventStart,
        end: comParcel.parcelComplementaryEventEnd,
        misrouting: !ends
      });
    });

    // Colloca ogni deviazione tra i due punti del percorso da cui parte e in cui rientra.
    // Un capo che non è un punto del percorso resta "libero": viene disegnato come punto fuori percorso,
    // all'altezza del punto successivo (o precedente) a quello di aggancio.
    const indexOfPoint = (point: RoutingChainPoint | null, fromIndex: number = 0): number =>
      point && point.code
        ? nodes.findIndex((node, i) => i >= fromIndex && this.isSameRoutingPoint(node.point, point))
        : -1;
    const lanes: { left: number; right: number }[][] = [];
    const detours: RoutingChainDetour[] = openDetours.map(detour => {
      let start = detour.startIndex ?? indexOfPoint(detour.start);
      const startAnchored = start >= 0;
      if (!startAnchored) {
        start = detour.reachedIndex;
      }

      let end = indexOfPoint(detour.end, start + 1);
      if (end < 0) {
        end = indexOfPoint(detour.end);
      }
      const endAnchored = end >= 0 && end !== start;
      if (!endAnchored) {
        end = start < nodes.length - 1 ? start + 1 : start - 1;
      }

      const backward = end < start;
      const left = Math.min(start, end);
      const right = Math.max(start, end);

      // Corsia: la più vicina al percorso in cui la deviazione non si sovrappone a un'altra
      let lane = lanes.findIndex(spans => spans.every(span => right <= span.left || left >= span.right));
      if (lane < 0) {
        lane = lanes.length;
        lanes.push([]);
      }
      lanes[lane].push({ left, right });

      return {
        gridColumn: `${2 * left + 1} / ${2 * right + 2}`,
        lane,
        backward,
        leftAnchored: backward ? endAnchored : startAnchored,
        rightAnchored: backward ? startAnchored : endAnchored,
        startAnchored,
        endAnchored,
        startName: detour.start?.name || 'Non definito',
        endName: detour.end?.name || 'Non definito',
        startPlace: detour.start?.place,
        endPlace: detour.end?.place,
        startCode: detour.start?.kind === 'warehouse' ? detour.start.code : undefined,
        startDamages: [],
        startPickings: [],
        legs: detour.legs
      };
    });

    // Eventi di danno (gli stessi della tab Eventi), raggruppati per magazzino e per finestra di 6 ore.
    // Ogni gruppo va sul punto di transito del magazzino in cui è stato registrato: sul percorso,
    // altrimenti sui punti fuori percorso delle deviazioni (legato al vettore della tappa che li raggiunge).
    // Senza magazzino, o con un magazzino che non compare in nessun punto, resta "non collocato"
    // e viene elencato sopra il percorso.
    const unplacedDamages: RoutingChainDamage[] = [];
    const damageGroups = this.groupDamageEvents();

    // Nella tabella degli orari i danni stanno solo sulla riga di transito nel magazzino (partenza e
    // arrivo coincidono): se il magazzino ha più righe di transito, su quella il cui intervallo contiene
    // il danno, altrimenti sulla prima
    const transitRows = schedule.filter(row => !!row.fromCode && row.fromCode === row.toCode);
    damageGroups.forEach(({ warehouseCode, damage }) => {
      const rows = transitRows.filter(row => row.fromCode === warehouseCode);
      const time = new Date(damage.date || '').getTime();
      const row = rows.find(candidate => {
        const start = new Date(candidate.start || '').getTime();
        const end = candidate.end ? new Date(candidate.end).getTime() : Infinity;
        return !isNaN(time) && !isNaN(start) && time >= start && time <= end;
      }) || rows[0];
      row?.damages.push(damage);
    });

    damageGroups.forEach(({ warehouseCode, damage }) => {
      const node = warehouseCode
        ? nodes.find(n => n.point.kind === 'warehouse' && n.point.code === warehouseCode)
        : undefined;

      if (node) {
        node.damages.push(damage);
      } else if (!warehouseCode || !this.placeDamageOnDetour(detours, warehouseCode, damage)) {
        unplacedDamages.push({ ...damage, place: warehouseCode ? this.getWarehouseName(warehouseCode) : undefined });
      }
    });

    // Eventi di picking: un gruppo per magazzino e finestra di 6 ore, sul punto di transito di quel magazzino
    this.groupPickingEvents().forEach(({ warehouseCode, picking }) => {
      const node = nodes.find(n => n.point.kind === 'warehouse' && n.point.code === warehouseCode);
      if (node) {
        node.pickings.push(picking);
      } else {
        this.placePickingOnDetour(detours, warehouseCode, picking);
      }
    });

    // Sotto un punto fuori percorso con danni serve spazio per le etichette: le corsie crescono di conseguenza
    const maxDamageGroups = detours.reduce((max, detour) =>
      Math.max(max, detour.startDamages.length, ...detour.legs.map(leg => leg.damages.length)), 0);
    this.routingChainDetourLaneHeight = maxDamageGroups > 0
      ? ROUTING_DETOUR_LANE_HEIGHT + ROUTING_DETOUR_DAMAGE_GAP + ROUTING_DAMAGE_CHIP_SPACING
          + maxDamageGroups * ROUTING_DAMAGE_CHIP_HEIGHT + (maxDamageGroups - 1) * ROUTING_DAMAGE_CHIP_SPACING
      : ROUTING_DETOUR_LANE_HEIGHT;

    this.routingChain = nodes;
    this.routingChainDetours = detours;
    this.routingChainDetourLanes = lanes.length;
    // Colonne della griglia: punto, tratta, punto, ... (le deviazioni si allineano ai punti)
    this.routingChainGridColumns = nodes
      .map((_, i) => i === 0 ? 'var(--chain-point-width)' : 'minmax(170px, 1fr) var(--chain-point-width)')
      .join(' ');
    this.routingChainUnplacedDamages = unplacedDamages;
    this.routingSchedule = schedule;
  }

  /**
   * Raggruppa gli eventi di danno per magazzino e per finestra di 6 ore: un evento entra nel gruppo
   * aperto del suo magazzino se è stato registrato entro 6 ore dal primo evento del gruppo,
   * altrimenti ne apre uno nuovo. I gruppi escono in ordine cronologico.
   */
  private groupDamageEvents(): { warehouseCode: string; damage: RoutingChainDamage }[] {
    const eventsByWarehouse = new Map<string, ComParcelEvent[]>();
    this.parcelEvents
      .filter(event => this.isDamageEvent(event))
      .sort((a, b) =>
        new Date(a.parcelEventComplementaryCreatedAt || 0).getTime() - new Date(b.parcelEventComplementaryCreatedAt || 0).getTime())
      .forEach(event => {
        const warehouseCode = event.parcelEventComplementaryWarehouseFrom || event.parcelEventComplementaryWarehouseTo || '';
        const events = eventsByWarehouse.get(warehouseCode) || [];
        events.push(event);
        eventsByWarehouse.set(warehouseCode, events);
      });

    const groups: { warehouseCode: string; damage: RoutingChainDamage }[] = [];
    eventsByWarehouse.forEach((events, warehouseCode) => {
      let current: ComParcelEvent[] = [];
      let windowStart = NaN;

      const flush = () => {
        if (current.length === 0) {
          return;
        }
        const first = current[0];
        const last = current[current.length - 1];
        const authors = Array.from(new Set(
          current.map(event => event.parcelEventComplementaryCreatedBy).filter((author): author is string => !!author)));
        groups.push({
          warehouseCode,
          damage: {
            date: first.parcelEventComplementaryCreatedAt,
            endDate: current.length > 1 ? last.parcelEventComplementaryCreatedAt : undefined,
            count: current.length,
            createdBy: authors.join(', ') || undefined
          }
        });
        current = [];
      };

      events.forEach(event => {
        const time = new Date(event.parcelEventComplementaryCreatedAt || '').getTime();
        // Senza data non si può escludere che appartenga al gruppo aperto
        const inWindow = current.length > 0
          && (isNaN(time) || isNaN(windowStart) || time - windowStart <= DAMAGE_GROUP_WINDOW_MS);
        if (!inWindow) {
          flush();
          windowStart = time;
        }
        current.push(event);
      });
      flush();
    });

    return groups.sort((a, b) =>
      new Date(a.damage.date || 0).getTime() - new Date(b.damage.date || 0).getTime());
  }

  /**
   * Raggruppa gli eventi di picking per magazzino e per finestra di 6 ore: un evento entra nel gruppo
   * aperto del suo magazzino se è stato registrato entro 6 ore dal primo del gruppo, altrimenti ne apre
   * uno nuovo. Di ogni gruppo si tiene la data del primo evento.
   */
  private groupPickingEvents(): { warehouseCode: string; picking: RoutingChainPicking }[] {
    const sorted = [...this.pickingEvents].sort((a, b) =>
      new Date(a.trackingCreatedAt || 0).getTime() - new Date(b.trackingCreatedAt || 0).getTime());

    const open = new Map<string, { picking: RoutingChainPicking; windowStart: number }>();
    const groups: { warehouseCode: string; picking: RoutingChainPicking }[] = [];

    sorted.forEach(event => {
      const warehouseCode = event.trackingWarehouseCode;
      const time = new Date(event.trackingCreatedAt || '').getTime();
      const current = open.get(warehouseCode);
      // Senza data non si può escludere che appartenga al gruppo aperto
      if (current && (isNaN(time) || isNaN(current.windowStart) || time - current.windowStart <= DAMAGE_GROUP_WINDOW_MS)) {
        current.picking.count++;
        return;
      }
      const picking: RoutingChainPicking = { date: event.trackingCreatedAt, count: 1 };
      open.set(warehouseCode, { picking, windowStart: time });
      groups.push({ warehouseCode, picking });
    });

    return groups;
  }

  /** Tooltip del badge di picking */
  getPickingTitle(picking: RoutingChainPicking): string {
    return picking.count > 1 ? `${picking.count} pickerate con il palmare` : 'Pickerata con il palmare';
  }

  /**
   * Colloca un gruppo di danno sul magazzino fuori percorso di una deviazione (misrouting).
   * Sui punti raggiunti da una tappa il danno si lega al vettore di quella tappa; l'arrivo di una
   * deviazione che rientra sul percorso non conta, perché è già un punto del percorso.
   * Restituisce false se nessuna deviazione ha quel magazzino come punto fuori percorso.
   */
  private placeDamageOnDetour(detours: RoutingChainDetour[], warehouseCode: string, damage: RoutingChainDamage): boolean {
    for (const detour of detours) {
      for (let i = 0; i < detour.legs.length; i++) {
        const leg = detour.legs[i];
        const isEnd = i === detour.legs.length - 1;
        if (leg.toCode === warehouseCode && (!isEnd || !detour.endAnchored)) {
          leg.damages.push(damage);
          return true;
        }
      }
      if (!detour.startAnchored && detour.startCode === warehouseCode) {
        detour.startDamages.push(damage);
        return true;
      }
    }
    return false;
  }

  /** Colloca un gruppo di picking sul magazzino fuori percorso di una deviazione (misrouting) */
  private placePickingOnDetour(detours: RoutingChainDetour[], warehouseCode: string, picking: RoutingChainPicking): void {
    for (const detour of detours) {
      for (let i = 0; i < detour.legs.length; i++) {
        const leg = detour.legs[i];
        const isEnd = i === detour.legs.length - 1;
        if (leg.toCode === warehouseCode && (!isEnd || !detour.endAnchored)) {
          leg.pickings.push(picking);
          return;
        }
      }
      if (!detour.startAnchored && detour.startCode === warehouseCode) {
        detour.startPickings.push(picking);
        return;
      }
    }
  }

  /** Quando finisce il gruppo: solo l'ora se è lo stesso giorno del primo evento, altrimenti data e ora */
  getDamageRangeLabel(damage: RoutingChainDamage): string {
    if (!damage.date || !damage.endDate) {
      return '';
    }
    const sameDay = formatDate(damage.date, 'yyyy-MM-dd', 'en-US') === formatDate(damage.endDate, 'yyyy-MM-dd', 'en-US');
    return '→ ' + formatDate(damage.endDate, sameDay ? 'HH:mm' : 'dd/MM/yyyy HH:mm', 'en-US');
  }

  /** Tooltip dell'etichetta di danno */
  getDamageTitle(damage: RoutingChainDamage): string {
    return [
      damage.count > 1 ? `${damage.count} eventi di danno` : 'Evento di danno',
      damage.createdBy ? `Segnalato da ${damage.createdBy}` : ''
    ].filter(part => !!part).join(' · ');
  }

  /** Numero totale di eventi di danno non collocabili (i gruppi possono contenerne più d'uno) */
  getUnplacedDamageCount(): number {
    return this.routingChainUnplacedDamages.reduce((total, damage) => total + damage.count, 0);
  }

  /**
   * Punti di transito e tratte del percorso a partire dalle tappe indicate.
   * Restituisce anche, per ogni tappa, i due nodi tra cui corre.
   */
  private buildRoutingChainNodes(legs: any[]): { nodes: RoutingChainNode[]; ends: Map<any, { from: RoutingChainNode; to: RoutingChainNode }> } {
    const nodes: RoutingChainNode[] = [];
    const ends = new Map<any, { from: RoutingChainNode; to: RoutingChainNode }>();

    // Aggiunge il punto solo se non coincide con l'ultimo inserito
    // (il "da" di una tappa è tipicamente l'"a" della precedente)
    const pushPoint = (point: RoutingChainPoint): RoutingChainNode => {
      const previous = nodes[nodes.length - 1];
      if (previous && this.isSameRoutingPoint(previous.point, point)) {
        return previous;
      }

      const node: RoutingChainNode = { point, damages: [], pickings: [] };
      nodes.push(node);
      return node;
    };
    const undefinedPoint = (): RoutingChainPoint => ({ code: '', name: 'Non definito', kind: 'warehouse' });

    let transportCount = 0;

    legs.forEach((comParcel, index) => {
      const isLast = index === legs.length - 1;
      const status = comParcel.parcelComplementaryStatus || '';

      // Partenza non valorizzata: la tappa riparte dall'ultimo punto raggiunto
      const fromPoint = this.resolveRoutingChainPoint(comParcel, 'from', false);
      const from = fromPoint
        ? pushPoint(fromPoint)
        : (nodes[nodes.length - 1] || pushPoint(undefinedPoint()));
      const to = pushPoint(this.resolveRoutingChainPoint(comParcel, 'to', isLast) || undefinedPoint());

      // Il punto di arrivo eredita lo stato della tappa che lo raggiunge;
      // il punto di partenza (senza tappa entrante) quello della tappa che ne parte
      to.point.status = status;
      if (from.point.status === undefined) {
        from.point.status = status;
      }

      // Le tappe che non cambiano punto (sosta nello stesso magazzino) non generano una tratta
      if (from !== to) {
        const carrierCode = this.resolveLegCarrierCode(comParcel);
        from.leg = {
          sequence: comParcel.parcelComplementarySequence || (this.comParcels.indexOf(comParcel) + 1),
          transport: ++transportCount,
          status,
          carrierCode,
          carrierName: this.getCarrierName(carrierCode)
        };
      }

      ends.set(comParcel, { from, to });
    });

    return { nodes, ends };
  }

  /** True per le tappe MISROUTING, per tipo o per stato (collo instradato per errore fuori dal percorso) */
  private isMisroutingLeg(comParcel: any): boolean {
    return (comParcel?.parcelComplementaryType || '').trim().toUpperCase() === 'MISROUTING'
      || (comParcel?.parcelComplementaryStatus || '').trim().toUpperCase() === 'MISROUTING';
  }

  /**
   * True per le scansioni fuori percorso: tappe con stato MISROUTING che il backend ricava dai tracking
   * del collo in un magazzino non previsto (partenza e arrivo coincidono, senza sequenza)
   */
  private isOffRouteScan(comParcel: any): boolean {
    return (comParcel?.parcelComplementaryStatus || '').trim().toUpperCase() === 'MISROUTING'
      && !!comParcel.parcelComplementaryWarehouseFrom
      && comParcel.parcelComplementaryWarehouseFrom === comParcel.parcelComplementaryWarehouseTo;
  }

  /** True se i due punti sono lo stesso magazzino o dealer (i punti senza codice non coincidono mai) */
  private isSameRoutingPoint(a: RoutingChainPoint | null, b: RoutingChainPoint | null): boolean {
    return !!a && !!b && !!a.code && a.code === b.code && a.kind === b.kind;
  }

  /**
   * La vista "Percorso" riporta anche gli eventi di danno: li carica se non ci sono ancora
   * (stessa fonte della tab Eventi; all'arrivo il percorso viene ricostruito)
   */
  private loadRoutingChainEvents(): void {
    if (this.routingViewMode !== 'chain' || this.isLoadingEvents) {
      return;
    }
    if (!this.parcel?.parcelCode || !this.parcel?.parcelWaybillCode || !this.parcel?.parcelShipmentCode) {
      return; // dati insufficienti per la chiamata: il percorso resta senza eventi di danno
    }

    this.loadEvents();
  }

  /**
   * Risolve un capo della tappa in un punto della vista "Percorso".
   * L'arrivo dell'ultima tappa è il dealer, gli altri capi sono magazzini (o dealer, se manca il magazzino).
   */
  private resolveRoutingChainPoint(comParcel: any, side: 'from' | 'to', isFinalDestination: boolean): RoutingChainPoint | null {
    const dealerCode = side === 'from' ? comParcel.parcelComplementaryDealerFrom : comParcel.parcelComplementaryDealerTo;

    if (isFinalDestination && side === 'to') {
      return {
        code: this.dealer?.dealerCode || dealerCode || 'DEALER',
        name: this.dealer?.dealerName || this.dealer?.dealerLegalName || dealerCode || 'Destinazione finale',
        kind: 'dealer',
        place: this.formatRoutingPlace(this.dealer?.dealerOperatingAddressCity, this.dealer?.dealerOperatingAddressProvince)
      };
    }

    const warehouseCode = side === 'from'
      ? comParcel.parcelComplementaryWarehouseFrom
      : comParcel.parcelComplementaryWarehouseTo;

    if (warehouseCode) {
      const warehouse = this.warehouseMap.get(warehouseCode);
      return {
        code: warehouseCode,
        name: this.getWarehouseName(warehouseCode),
        kind: 'warehouse',
        place: this.formatRoutingPlace(warehouse?.warehouseAddressCity, warehouse?.warehouseAddressProvince)
      };
    }

    return dealerCode ? { code: dealerCode, name: dealerCode, kind: 'dealer' } : null;
  }

  /** Località su una riga: "Città (PR)" */
  private formatRoutingPlace(city: string | undefined, province: string | undefined): string {
    return [city, province ? `(${province})` : ''].filter(part => !!part).join(' ');
  }

  /**
   * Codice vettore di una tappa, con la stessa priorità della vista tracking:
   * loading list, gruppo di default del tour routing, vettore della tappa
   */
  private resolveLegCarrierCode(comParcel: any): string | undefined {
    return comParcel.loadingListCarrierCode
      || comParcel.carrierCodeDefaultGroup
      || comParcel.parcelComplementaryCarrierCode
      || comParcel.carrierCode
      || undefined;
  }

  /** Nome dell'azienda trasportatrice dal codice vettore (il codice stesso se non è in anagrafica) */
  private getCarrierName(carrierCode: string | undefined): string {
    if (!carrierCode) return '';

    const carrier = (this.carriers || []).find(c => c.carrierCode === carrierCode);
    return carrier?.carrierName || carrierCode;
  }

  /**
   * Etichetta italiana dello stato di una tappa (transcodifica COM_PARCEL_STATUS_LABELS);
   * uno stato non mappato resta com'è
   */
  getRoutingStatusLabel(status: string | undefined | null): string {
    const key = (status || '').trim().toUpperCase();
    return COM_PARCEL_STATUS_LABELS[key] ?? key;
  }

  /** Classe CSS dello stato di una tappa (es. IN_PROGRESS → status-in-progress) */
  getRoutingStatusClass(status: string | undefined): string {
    return status ? 'status-' + status.toLowerCase().replace(/_/g, '-') : '';
  }

  /**
   * Costruisce punti di transito e tratte georeferenziate a partire dai ComParcels.
   * I punti senza coordinate (magazzino non geolocalizzato o dealer senza lat/lng)
   * vengono esclusi, e con essi le tratte che li toccano.
   */
  private buildRoutingGeoData(): void {
    this.routingMapPoints = [];
    this.routingMapLegs = [];
    this.routingMapOffRoutePoints = [];

    if (!this.comParcels || this.comParcels.length === 0) {
      return;
    }

    const points: RoutingMapPoint[] = [];

    // Aggiunge il punto solo se non coincide con l'ultimo inserito
    // (il "da" di una tappa è tipicamente l'"a" della precedente)
    const pushPoint = (point: RoutingMapPoint | null): RoutingMapPoint | null => {
      if (!point) return null;

      const previous = points[points.length - 1];
      if (previous && previous.code === point.code && previous.kind === point.kind) {
        return previous;
      }

      point.sequence = points.length + 1;
      points.push(point);
      return point;
    };

    // Le scansioni fuori percorso (misrouting) non sono tratte: diventano punti a sé, non collegati
    const legs = this.comParcels.filter(comParcel => !this.isOffRouteScan(comParcel));
    this.routingMapOffRoutePoints = this.comParcels
      .filter(comParcel => this.isOffRouteScan(comParcel))
      .map(comParcel => this.resolveRoutingPoint(comParcel, 'from', false))
      .filter((point): point is RoutingMapPoint => !!point)
      .map(point => ({ ...point, status: 'MISROUTING' }));

    legs.forEach((comParcel, index) => {
      const isLast = index === legs.length - 1;
      const from = pushPoint(this.resolveRoutingPoint(comParcel, 'from', false));
      const to = pushPoint(this.resolveRoutingPoint(comParcel, 'to', isLast));

      const status = comParcel.parcelComplementaryStatus || '';
      const type = comParcel.parcelComplementaryType || '';

      // Il punto di arrivo eredita stato e tipo della tappa che lo raggiunge;
      // il punto di partenza (senza tappa entrante) quelli della tappa che ne parte
      if (to) {
        to.status = status;
        to.type = type;
      }
      if (from && from.status === undefined) {
        from.status = status;
        from.type = type;
      }

      if (from && to && from !== to) {
        this.routingMapLegs.push({
          from,
          to,
          sequence: comParcel.parcelComplementarySequence || (index + 1),
          type: comParcel.parcelComplementaryType || '',
          status: comParcel.parcelComplementaryStatus || '',
          carrierCode: this.resolveLegCarrierCode(comParcel),
          distance: this.calculateDistance(from.latitude, from.longitude, to.latitude, to.longitude),
          eventStart: comParcel.parcelComplementaryEventStart,
          eventEnd: comParcel.parcelComplementaryEventEnd
        });
      }
    });

    this.routingMapPoints = points;
  }

  /**
   * Risolve un capo della tratta in un punto georeferenziato.
   * L'arrivo dell'ultima tappa è il dealer, gli altri capi sono magazzini.
   */
  private resolveRoutingPoint(comParcel: any, side: 'from' | 'to', isFinalDestination: boolean): RoutingMapPoint | null {
    if (isFinalDestination && side === 'to') {
      const dealerLat = this.dealer?.dealerLatitude;
      const dealerLng = this.dealer?.dealerLongitude;

      if (!dealerLat || !dealerLng) {
        return null;
      }

      return {
        sequence: 0,
        code: this.dealer?.dealerCode || comParcel.parcelComplementaryDealerTo || 'DEALER',
        name: this.dealer?.dealerName || this.dealer?.dealerLegalName || comParcel.parcelComplementaryDealerTo || 'Destinazione finale',
        address: this.getDealerAddressLine(),
        latitude: dealerLat,
        longitude: dealerLng,
        kind: 'dealer'
      };
    }

    const warehouseCode = side === 'from'
      ? comParcel.parcelComplementaryWarehouseFrom
      : comParcel.parcelComplementaryWarehouseTo;

    if (!warehouseCode) {
      return null;
    }

    const warehouse = this.warehouseMap.get(warehouseCode);
    if (!warehouse?.warehouseLatitude || !warehouse?.warehouseLongitude) {
      return null;
    }

    return {
      sequence: 0,
      code: warehouseCode,
      name: warehouse.warehouseName || warehouse.warehouseShortName || warehouseCode,
      address: this.getWarehouseAddressLine(warehouse),
      latitude: warehouse.warehouseLatitude,
      longitude: warehouse.warehouseLongitude,
      kind: 'warehouse'
    };
  }

  /**
   * Indirizzo del magazzino su una riga (via, civico, CAP, città, provincia)
   */
  private getWarehouseAddressLine(warehouse: any): string {
    const street = [warehouse?.warehouseAddressStreet, warehouse?.warehouseAddressNumber]
      .filter(part => !!part).join(' ');
    const city = [warehouse?.warehouseAddressZip, warehouse?.warehouseAddressCity]
      .filter(part => !!part).join(' ');
    const province = warehouse?.warehouseAddressProvince ? `(${warehouse.warehouseAddressProvince})` : '';

    return [street, city, province].filter(part => !!part).join(', ');
  }

  /**
   * Indirizzo operativo del dealer su una riga
   */
  private getDealerAddressLine(): string {
    const street = [this.dealer?.dealerOperatingAddressStreet, this.dealer?.dealerOperatingAddressNumber]
      .filter(part => !!part).join(' ');
    const city = [this.dealer?.dealerOperatingAddressZip, this.dealer?.dealerOperatingAddressCity]
      .filter(part => !!part).join(' ');
    const province = this.dealer?.dealerOperatingAddressProvince ? `(${this.dealer.dealerOperatingAddressProvince})` : '';

    return [street, city, province].filter(part => !!part).join(', ');
  }

  /**
   * Colore della tratta e del marker in base allo stato della tappa
   */
  private getRoutingStatusColor(status: string | undefined): string {
    switch ((status || '').toUpperCase()) {
      case 'EXECUTED':
      case 'COMPLETED':
        return '#198754';
      case 'IN_PROGRESS':
        return '#0d6efd';
      case 'FAILED':
        return '#dc3545';
      case 'PENDING':
      case 'UNKNOWN':
        return '#fd7e14';
      case 'PLANNED':
      default:
        return '#6c757d';
    }
  }

  /**
   * True se la tappa non è ancora stata eseguita (linea tratteggiata)
   */
  private isRoutingLegPending(status: string | undefined): boolean {
    return !['EXECUTED', 'COMPLETED', 'IN_PROGRESS'].includes((status || '').toUpperCase());
  }

  /**
   * Distanza leggibile (m sotto il chilometro, km con un decimale sopra)
   */
  formatRoutingDistance(meters: number): string {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(1).replace('.', ',')} km`;
  }

  /**
   * Escape dei caratteri HTML per i contenuti iniettati in popup/label Leaflet
   */
  private escapeHtml(value: string | undefined | null): string {
    if (!value) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Data leggibile per le card della mappa (vuota se non valorizzata)
   */
  private formatRoutingDate(value: string | undefined): string {
    if (!value) return '';

    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date.toLocaleString('it-IT');
  }

  /**
   * Card di dettaglio della tratta, mostrata cliccando la linea o la sua icona info.
   * La distanza è marcata esplicitamente come "Linea d'aria" perché calcolata
   * con la formula di Haversine tra i due punti, non sul percorso stradale.
   */
  private buildRoutingLegCard(leg: RoutingMapLeg, color: string): string {
    const typeIcon = leg.type === 'TRANSIT' ? 'fa-warehouse' : (leg.type === 'TRANSPORT' ? 'fa-truck' : 'fa-route');
    const eventStart = this.formatRoutingDate(leg.eventStart);
    const eventEnd = this.formatRoutingDate(leg.eventEnd);

    return `
      <div class="routing-map-card">
        <div class="routing-map-card-header" style="background: ${color};">
          <span class="map-card-seq">Tappa ${leg.sequence}</span>
          <span class="map-card-type">
            <i class="fas ${typeIcon}"></i> ${this.escapeHtml(leg.type) || 'N/A'}
          </span>
        </div>

        <div class="routing-map-card-body">
          <!-- Tratta: da → a -->
          <div class="leg-card-route">
            <div class="leg-card-point">
              <span class="map-card-label">Da</span>
              <span class="leg-card-value">${this.escapeHtml(leg.from.name)}</span>
            </div>
            <i class="fas fa-arrow-right leg-card-arrow"></i>
            <div class="leg-card-point">
              <span class="map-card-label">A</span>
              <span class="leg-card-value">${this.escapeHtml(leg.to.name)}</span>
            </div>
          </div>

          <!-- Blocco distanza: esplicita che è in linea d'aria -->
          <div class="map-card-block">
            <div class="map-card-block-top">
              <span class="map-card-block-value">
                <i class="fas fa-ruler-combined"></i> ${this.formatRoutingDistance(leg.distance)}
              </span>
              <span class="map-card-block-badge">
                <i class="fas fa-plane"></i> Linea d'aria
              </span>
            </div>
            <p class="map-card-block-note">
              Distanza geodetica calcolata tra le coordinate dei due punti.
              Non corrisponde ai chilometri effettivamente percorsi su strada.
            </p>
          </div>

          <!-- Dettagli tappa -->
          <div class="map-card-rows">
            <div class="map-card-row">
              <i class="fas fa-circle-notch"></i>
              <span class="map-row-label">Stato</span>
              <span class="map-row-value" style="color: ${color};">
                ${this.escapeHtml(this.getRoutingStatusLabel(leg.status))}
              </span>
            </div>
            ${leg.carrierCode ? `
            <div class="map-card-row">
              <i class="fas fa-truck"></i>
              <span class="map-row-label">Vettore</span>
              <span class="map-row-value">${this.escapeHtml(leg.carrierCode)}</span>
            </div>` : ''}
            ${eventStart ? `
            <div class="map-card-row">
              <i class="fas fa-play"></i>
              <span class="map-row-label">Inizio</span>
              <span class="map-row-value">${this.escapeHtml(eventStart)}</span>
            </div>` : ''}
            ${eventEnd ? `
            <div class="map-card-row">
              <i class="fas fa-flag-checkered"></i>
              <span class="map-row-label">Fine</span>
              <span class="map-row-value">${this.escapeHtml(eventEnd)}</span>
            </div>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Card di dettaglio del punto di transito, mostrata cliccando il marker.
   * Usa la stessa struttura e lo stesso colore di stato della card delle tratte.
   */
  private buildRoutingPointCard(point: RoutingMapPoint, roleLabel: string, color: string): string {
    const kindIcon = point.kind === 'dealer' ? 'fa-store' : 'fa-warehouse';
    const kindLabel = point.kind === 'dealer' ? 'DEALER' : 'MAGAZZINO';

    return `
      <div class="routing-map-card">
        <div class="routing-map-card-header" style="background: ${color};">
          <span class="map-card-seq">${point.sequence ? `Punto ${point.sequence}` : 'Fuori percorso'}</span>
          <span class="map-card-type">
            <i class="fas ${kindIcon}"></i> ${kindLabel}
          </span>
        </div>

        <div class="routing-map-card-body">
          <!-- Denominazione e ruolo nell'instradamento -->
          <div class="point-card-identity">
            <span class="map-card-label">${roleLabel}</span>
            <span class="point-card-name">${this.escapeHtml(point.name)}</span>
          </div>

          <!-- Blocco coordinate, speculare al blocco distanza delle tratte -->
          <div class="map-card-block">
            <div class="map-card-block-top">
              <span class="map-card-block-value">
                <i class="fas fa-location-crosshairs"></i> ${point.latitude}, ${point.longitude}
              </span>
              <span class="map-card-block-badge point-badge">
                <i class="fas fa-map-pin"></i> Coordinate
              </span>
            </div>
            ${point.address ? `<p class="map-card-block-note">${this.escapeHtml(point.address)}</p>` : ''}
          </div>

          <!-- Dettagli punto -->
          <div class="map-card-rows">
            <div class="map-card-row">
              <i class="fas fa-circle-notch"></i>
              <span class="map-row-label">Stato</span>
              <span class="map-row-value" style="color: ${color};">
                ${this.escapeHtml(this.getRoutingStatusLabel(point.status))}
              </span>
            </div>
            ${point.type ? `
            <div class="map-card-row">
              <i class="fas fa-route"></i>
              <span class="map-row-label">Tappa</span>
              <span class="map-row-value">${this.escapeHtml(point.type)}</span>
            </div>` : ''}
            <div class="map-card-row">
              <i class="fas fa-hashtag"></i>
              <span class="map-row-label">Codice</span>
              <span class="map-row-value">${this.escapeHtml(point.code)}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Disegna la mappa dell'instradamento: un marker per ogni punto di transito,
   * una linea per ogni tratta, entrambi con label permanente
   */
  private initializeRoutingMap(): void {
    if (!this.routingMapContainer || this.routingMapPoints.length === 0) {
      return;
    }

    this.destroyRoutingMap();

    this.routingMap = L.map(this.routingMapContainer.nativeElement, {
      zoomControl: true,
      attributionControl: true
    });

    this.applyRoutingMapStyle();

    // Linee delle tratte (disegnate prima dei marker per restare sotto)
    this.routingMapLegs.forEach(leg => {
      const color = this.getRoutingStatusColor(leg.status);

      const polyline = L.polyline(
        [[leg.from.latitude, leg.from.longitude], [leg.to.latitude, leg.to.longitude]],
        {
          color,
          weight: 4,
          opacity: 0.85,
          dashArray: this.isRoutingLegPending(leg.status) ? '8, 8' : undefined
        }
      ).addTo(this.routingMap!);

      // Label sulla linea: tappa, tipo, distanza in linea d'aria e icona info cliccabile
      const legLabelText = [`Tappa ${leg.sequence}`, leg.type]
        .filter(part => !!part)
        .map(part => this.escapeHtml(String(part)))
        .join(' · ');

      polyline.bindTooltip(`
        <span class="routing-line-label-text">${legLabelText}</span>
        <span class="routing-line-label-distance">
          <i class="fas fa-ruler-combined"></i> ${this.formatRoutingDistance(leg.distance)}
        </span>
        <i class="fas fa-info-circle routing-line-label-info" title="Dettagli tratta"></i>
      `, {
        permanent: true,
        direction: 'center',
        className: 'routing-line-label',
        opacity: 1,
        interactive: true // rende cliccabile la label: apre la card della tratta
      });

      polyline.bindPopup(this.buildRoutingLegCard(leg, color), {
        className: 'routing-map-card-popup',
        maxWidth: 320,
        minWidth: 260
      });

      this.routingMapLegLayers.push(polyline);
    });

    // Marker dei punti di transito
    this.routingMapPoints.forEach((point, index) => {
      const isFinal = index === this.routingMapPoints.length - 1;
      const isFirst = index === 0;

      const icon = L.divIcon({
        className: 'routing-marker-icon',
        html: `
          <div class="routing-marker ${point.kind === 'dealer' ? 'is-dealer' : ''} ${isFirst ? 'is-first' : ''} ${isFinal ? 'is-final' : ''}">
            <i class="fas ${point.kind === 'dealer' ? 'fa-store' : 'fa-warehouse'}"></i>
            <span class="routing-marker-seq">${point.sequence}</span>
          </div>
        `,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
        popupAnchor: [0, -20]
      });

      const marker = L.marker([point.latitude, point.longitude], { icon, zIndexOffset: 500 }).addTo(this.routingMap!);

      marker.bindTooltip(
        `<span class="routing-point-label-seq">${point.sequence}</span>${this.escapeHtml(point.name)}`,
        {
          permanent: true,
          direction: 'top',
          offset: [0, -16],
          className: `routing-point-label ${point.kind === 'dealer' ? 'is-dealer' : ''}`,
          opacity: 1
        }
      );

      const roleLabel = point.kind === 'dealer'
        ? 'Destinazione finale (Dealer)'
        : (isFirst ? 'Punto di partenza' : 'Punto di transito');

      // Stesso colore di stato usato per la tratta collegata al punto
      const pointColor = this.getRoutingStatusColor(point.status);

      marker.bindPopup(this.buildRoutingPointCard(point, roleLabel, pointColor), {
        className: 'routing-map-card-popup',
        maxWidth: 320,
        minWidth: 260
      });

      this.routingMapPointLayers.push(marker);
    });

    // Marker dei punti fuori percorso (scansioni misrouting): senza progressivo e non collegati alle tratte
    this.routingMapOffRoutePoints.forEach(point => {
      const icon = L.divIcon({
        className: 'routing-marker-icon',
        html: `
          <div class="routing-marker is-misrouting">
            <i class="fas fa-shuffle"></i>
          </div>
        `,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
        popupAnchor: [0, -20]
      });

      const marker = L.marker([point.latitude, point.longitude], { icon, zIndexOffset: 400 }).addTo(this.routingMap!);

      marker.bindTooltip(`Misrouting · ${this.escapeHtml(point.name)}`, {
        permanent: true,
        direction: 'top',
        offset: [0, -16],
        className: 'routing-point-label is-misrouting',
        opacity: 1
      });

      marker.bindPopup(this.buildRoutingPointCard(point, 'Punto fuori percorso (Misrouting)', ROUTING_MAP_MISROUTING_COLOR), {
        className: 'routing-map-card-popup',
        maxWidth: 320,
        minWidth: 260
      });

      this.routingMapPointLayers.push(marker);
    });

    // Inquadra tutti i punti dell'instradamento, fuori percorso compresi
    const bounds = L.latLngBounds(
      [...this.routingMapPoints, ...this.routingMapOffRoutePoints]
        .map(point => [point.latitude, point.longitude] as L.LatLngTuple)
    );
    this.routingMap.fitBounds(bounds, { padding: [60, 60], maxZoom: 15 });

    // Ripristina lo stato dei due gruppi di etichette scelto dall'utente
    if (!this.routingMapPointLabelsVisible) {
      this.routingMapPointLayers.forEach(layer => layer.closeTooltip());
    }
    if (!this.routingMapLegLabelsVisible) {
      this.routingMapLegLayers.forEach(layer => layer.closeTooltip());
    }

    setTimeout(() => {
      if (this.routingMap) {
        this.routingMap.invalidateSize();
      }
    }, 100);
  }

  /**
   * Stile di mappa attualmente selezionato
   */
  get currentRoutingMapStyle(): RoutingMapStyle {
    return this.routingMapStyles.find(style => style.id === this.routingMapStyleId) || this.routingMapStyles[0];
  }

  /**
   * Cambia il basemap sostituendo solo il tile layer (la mappa non viene ricreata)
   */
  setRoutingMapStyle(styleId: RoutingMapStyleId): void {
    if (this.routingMapStyleId === styleId) {
      return;
    }

    this.routingMapStyleId = styleId;
    this.applyRoutingMapStyle();
  }

  /**
   * Applica alla mappa il tile layer dello stile selezionato
   */
  private applyRoutingMapStyle(): void {
    if (!this.routingMap) {
      return;
    }

    if (this.routingMapTileLayer) {
      this.routingMap.removeLayer(this.routingMapTileLayer);
      this.routingMapTileLayer = null;
    }

    const style = this.currentRoutingMapStyle;

    this.routingMapTileLayer = L.tileLayer(style.url, {
      attribution: style.attribution,
      maxZoom: 19,
      detectRetina: true
    }).addTo(this.routingMap);

    // Una sola classe di stile alla volta: filtra le tile e adatta le label
    const container = this.routingMap.getContainer();
    this.routingMapStyles.forEach(s => container.classList.remove(s.cssClass));
    container.classList.add(style.cssClass);
  }

  /**
   * Mostra/nasconde le label permanenti dei punti di transito
   * (utili da nascondere quando le tappe sono ravvicinate)
   */
  toggleRoutingMapPointLabels(): void {
    this.routingMapPointLabelsVisible = !this.routingMapPointLabelsVisible;
    this.applyTooltipVisibility(this.routingMapPointLayers, this.routingMapPointLabelsVisible);
  }

  /**
   * Mostra/nasconde le label permanenti delle tratte
   */
  toggleRoutingMapLegLabels(): void {
    this.routingMapLegLabelsVisible = !this.routingMapLegLabelsVisible;
    this.applyTooltipVisibility(this.routingMapLegLayers, this.routingMapLegLabelsVisible);
  }

  /**
   * Apre o chiude i tooltip permanenti di un gruppo di layer
   */
  private applyTooltipVisibility(layers: L.Layer[], visible: boolean): void {
    layers.forEach(layer => {
      if (visible) {
        layer.openTooltip();
      } else {
        layer.closeTooltip();
      }
    });
  }

  /**
   * Ricentra la mappa su tutti i punti dell'instradamento
   */
  fitRoutingMap(): void {
    if (!this.routingMap || this.routingMapPoints.length === 0) {
      return;
    }

    const bounds = L.latLngBounds(
      [...this.routingMapPoints, ...this.routingMapOffRoutePoints]
        .map(point => [point.latitude, point.longitude] as L.LatLngTuple)
    );
    this.routingMap.fitBounds(bounds, { padding: [60, 60], maxZoom: 15 });
  }

  /**
   * Numero di tappe escluse dalla mappa perché prive di coordinate
   */
  get routingMapMissingLegs(): number {
    const legs = this.comParcels.filter(comParcel => !this.isOffRouteScan(comParcel));
    return Math.max(0, legs.length - this.routingMapLegs.length);
  }

  /**
   * Ricostruisce e ridisegna la mappa se la vista mappa è quella attiva
   * (chiamata al termine del caricamento dei ComParcels)
   */
  private refreshRoutingMapIfActive(): void {
    if (this.routingViewMode !== 'map' || this.activeTab !== 'routing') {
      return;
    }

    this.buildRoutingGeoData();
    setTimeout(() => this.initializeRoutingMap(), 100);
  }

  /**
   * Distrugge la mappa dell'instradamento e azzera i layer con label
   */
  private destroyRoutingMap(): void {
    this.routingMapPointLayers = [];
    this.routingMapLegLayers = [];
    this.routingMapTileLayer = null;

    if (this.routingMap) {
      this.routingMap.remove();
      this.routingMap = null;
    }
  }

    setInvalidParcelForLoadingLists(): void {
    if (!this.parcel || !this.parcel.parcelCode) {
      this.notificationService.error('Nessun collo selezionato');
      return;
    }

    // Emetti l'evento al componente padre per aprire il popup
    this.openInvalidateLoadingListsPopup.emit([this.parcel.parcelCode]);
  }

  openLoadingListsPopupForCurrentParcel(): void {
    if (!this.parcel) return;

    this.loadingListsPopupParcels = [
      {
        parcelCode: this.parcel.parcelCode,
        parcelInactiveForLoadingLists: this.parcel.parcelInactiveForLoadingLists
      }
    ];

    this.loadingListsPopupInitialTab = this.parcel.parcelInactiveForLoadingLists ? 'validate' : 'invalidate';
    
    this.openInvalidateLoadingListsPopup.emit([this.parcel.parcelCode]);
  }
}
